// src/pages/AdminDiets.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Container, Box, Typography, Button, Card, CardContent,
    Grid, IconButton, Dialog, DialogTitle, DialogContent,
    DialogActions, TextField, Alert, Chip, Avatar,
    Snackbar, CircularProgress, useTheme
} from '@mui/material';
import {
    Add, Edit, Delete, Restaurant, KeyboardArrowLeft,
    Fastfood, DragIndicator, Save
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import AdminHeader from '../components/AdminHeader';
import { dietAPI } from '../services/api';

const AdminDiets = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const { user, isAuthenticated, loading: authLoading } = useAuth();

    const [diets, setDiets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingDiet, setEditingDiet] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState({ open: false, diet: null });
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [formData, setFormData] = useState({ name: '', description: '', price: '' });
    const [formErrors, setFormErrors] = useState({});

    useEffect(() => {
        if (!authLoading && (!isAuthenticated || user?.role !== 'ADMIN')) {
            navigate('/login');
        }
    }, [authLoading, isAuthenticated, user, navigate]);

    const fetchDiets = async () => {
        try {
            setLoading(true);
            const data = await dietAPI.admin.getAll();
            setDiets(Array.isArray(data) ? data : []);
        } catch (error) {
            showSnackbar('Грешка при вчитување на диети', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!authLoading && isAuthenticated && user?.role === 'ADMIN') {
            fetchDiets();
        }
    }, [authLoading, isAuthenticated, user]);

    const showSnackbar = (message, severity = 'success') =>
        setSnackbar({ open: true, message, severity });

    // ===== DRAG & DROP =====
    const handleDragStart = (e, index) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
        e.target.style.opacity = '0.5';
    };
    const handleDragEnd = (e) => {
        e.target.style.opacity = '1';
        setDraggedIndex(null);
    };
    const handleDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };
    const handleDrop = async (e, targetIndex) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === targetIndex) return;
        const newDiets = [...diets];
        const [dragged] = newDiets.splice(draggedIndex, 1);
        newDiets.splice(targetIndex, 0, dragged);
        setDiets(newDiets);
        setDraggedIndex(null);
        showSnackbar('Редоследот е успешно ажуриран! 🔄');
    };

    // ===== FORM =====
    const handleOpenDialog = (diet = null) => {
        if (diet) {
            setEditingDiet(diet);
            setFormData({ name: diet.name, description: diet.description, price: diet.price.toString() });
        } else {
            setEditingDiet(null);
            setFormData({ name: '', description: '', price: '' });
        }
        setFormErrors({});
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingDiet(null);
        setFormData({ name: '', description: '', price: '' });
        setFormErrors({});
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.name.trim()) errors.name = 'Името е задолжително';
        if (!formData.description.trim()) errors.description = 'Описот е задолжителен';
        if (!formData.price || isNaN(formData.price) || parseFloat(formData.price) <= 0)
            errors.price = 'Цената мора да биде валиден позитивен број';
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // ===== SAVE =====
    const handleSaveDiet = async () => {
        if (!validateForm()) return;
        try {
            setSaving(true);
            const dietData = {
                name: formData.name.trim(),
                description: formData.description.trim(),
                price: parseFloat(formData.price)
            };
            if (editingDiet) {
                await dietAPI.admin.update(editingDiet.id, dietData);
                showSnackbar('Диетата е успешно ажурирана! 🎉');
            } else {
                await dietAPI.admin.create(dietData);
                showSnackbar('Диетата е успешно креирана! 🎉');
            }
            handleCloseDialog();
            fetchDiets();
        } catch (error) {
            showSnackbar('Грешка при зачувување: ' + (error.response?.data?.message || error.message), 'error');
        } finally {
            setSaving(false);
        }
    };

    // ===== DELETE =====
    const handleDeleteDiet = async () => {
        if (!deleteConfirm.diet) return;
        try {
            setDeleteLoading(true);
            await dietAPI.admin.delete(deleteConfirm.diet.id);
            showSnackbar('Диетата е успешно избришана! 🗑️');
            setDeleteConfirm({ open: false, diet: null });
            fetchDiets();
        } catch (error) {
            showSnackbar('Грешка при бришење на диетата', 'error');
        } finally {
            setDeleteLoading(false);
        }
    };

    const fieldSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            '&:hover fieldset': { borderColor: '#ff7eb9' },
            '&.Mui-focused fieldset': { borderColor: '#ff7eb9' }
        },
        '& .MuiInputLabel-root.Mui-focused': { color: '#ff7eb9' }
    };

    // ===== LOADING =====
    if (authLoading || loading) {
        return (
            <Box sx={{
                minHeight: '100vh',
                backgroundColor: theme.palette.background.default,
                display: 'flex', justifyContent: 'center', alignItems: 'center'
            }}>
                <CircularProgress sx={{ color: '#ff7eb9' }} />
                <Typography variant="h6" sx={{ ml: 2, color: theme.palette.text.primary }}>
                    Вчитување...
                </Typography>
            </Box>
        );
    }

    if (!isAuthenticated || user?.role !== 'ADMIN') return null;

    return (
        <Box sx={{ py: 4, minHeight: '100vh', backgroundColor: theme.palette.background.default }}>
            <Container maxWidth="lg">
                <AdminHeader />

                {/* ===== BACK BUTTON ===== */}
                <Box sx={{ mb: 4 }}>
                    <Button
                        onClick={() => navigate('/admin')}
                        startIcon={<KeyboardArrowLeft sx={{ fontSize: '28px !important' }} />}
                        variant="outlined"
                        sx={{
                            color: '#ff7eb9', borderColor: '#ff7eb9',
                            backgroundColor: 'rgba(255,126,185,0.06)',
                            fontWeight: 'bold', fontSize: '1rem',
                            px: 3, py: 1.5, borderRadius: '12px',
                            textTransform: 'none', transition: 'all 0.3s ease',
                            '&:hover': {
                                backgroundColor: '#ff7eb9', color: 'white',
                                borderColor: '#ff4a97', transform: 'translateY(-2px)',
                                boxShadow: '0 6px 20px rgba(255,126,185,0.3)'
                            }
                        }}
                    >
                        Назад кон Admin Панел
                    </Button>
                </Box>

                {/* ===== HEADER ===== */}
                <Box sx={{ mb: 6, textAlign: 'center' }}>
                    <Box sx={{
                        width: 72, height: 72, borderRadius: '50%',
                        mx: 'auto', mb: 3,
                        background: 'linear-gradient(135deg, #ff7eb9, #AB47BC)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: '0 8px 32px rgba(255,126,185,0.35)',
                        fontSize: '2rem'
                    }}>
                        🥗
                    </Box>
                    <Typography variant="h2" sx={{
                        fontWeight: 900, mb: 1,
                        background: 'linear-gradient(45deg, #ff7eb9, #AB47BC)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        fontSize: { xs: '2rem', md: '3rem' }
                    }}>
                        Управување со Диети
                    </Typography>
                    <Typography variant="h6" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
                        Додавај, уредувај и бришете диететски планови
                    </Typography>
                    <Box sx={{
                        width: 60, height: 4, borderRadius: 2,
                        background: 'linear-gradient(90deg, #ff7eb9, #AB47BC)',
                        mx: 'auto', mb: 3
                    }} />

                    <Box display="flex" justifyContent="center" alignItems="center" gap={2} flexWrap="wrap">
                        <Chip label={`${diets.length} диети`} sx={{
                            bgcolor: 'rgba(255,126,185,0.1)', color: '#ff7eb9',
                            fontWeight: 'bold', border: '1px solid rgba(255,126,185,0.3)'
                        }} />
                        {diets.length > 1 && (
                            <Chip label="🔄 Влечи и пушти за редослед" variant="outlined" size="small"
                                  sx={{ borderColor: '#ff7eb9', color: '#ff7eb9' }} />
                        )}
                        <Button variant="contained" startIcon={<Add />}
                                onClick={() => handleOpenDialog()}
                                sx={{
                                    background: 'linear-gradient(45deg, #ff7eb9, #AB47BC)',
                                    px: 3, py: 1, borderRadius: '12px',
                                    fontWeight: 'bold', textTransform: 'none',
                                    boxShadow: '0 4px 15px rgba(255,126,185,0.3)',
                                    '&:hover': {
                                        background: 'linear-gradient(45deg, #ff4a97, #9C27B0)',
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 8px 25px rgba(255,126,185,0.4)'
                                    },
                                    transition: 'all 0.3s ease'
                                }}>
                            Додади Диета
                        </Button>
                    </Box>
                </Box>

                {/* ===== NO DIETS ===== */}
                {diets.length === 0 ? (
                    <Box sx={{
                        textAlign: 'center', py: 12,
                        border: '2px dashed rgba(255,126,185,0.3)',
                        borderRadius: '20px',
                        backgroundColor: theme.palette.mode === 'dark'
                            ? 'rgba(255,126,185,0.05)'
                            : 'rgba(255,126,185,0.03)'
                    }}>
                        <Fastfood sx={{ fontSize: 80, color: '#ff7eb9', mb: 3 }} />
                        <Typography variant="h4" sx={{ color: '#ff7eb9', mb: 2, fontWeight: 'bold' }}>
                            Нема диети
                        </Typography>
                        <Typography variant="h6" sx={{
                            color: theme.palette.text.secondary, mb: 4,
                            maxWidth: 500, mx: 'auto'
                        }}>
                            Започнете со додавање на првата диета
                        </Typography>
                        <Button variant="contained" startIcon={<Add />}
                                onClick={() => handleOpenDialog()}
                                sx={{
                                    background: 'linear-gradient(45deg, #ff7eb9, #AB47BC)',
                                    px: 5, py: 1.5, borderRadius: '12px',
                                    fontSize: '1.1rem', fontWeight: 'bold', textTransform: 'none',
                                    '&:hover': { background: 'linear-gradient(45deg, #ff4a97, #9C27B0)' }
                                }}>
                            Додади Прва Диета
                        </Button>
                    </Box>
                ) : (
                    /* ===== DIETS GRID ===== */
                    <Grid container spacing={4} justifyContent="center">
                        {diets.map((diet, index) => (
                            <Grid item xs={12} sm={6} md={4} key={diet.id}
                                  sx={{ display: 'flex', justifyContent: 'center' }}>
                                <Card
                                    draggable
                                    onDragStart={(e) => handleDragStart(e, index)}
                                    onDragEnd={handleDragEnd}
                                    onDragOver={handleDragOver}
                                    onDrop={(e) => handleDrop(e, index)}
                                    sx={{
                                        width: '100%', maxWidth: 380,
                                        display: 'flex', flexDirection: 'column',
                                        borderRadius: '20px',
                                        backgroundColor: theme.palette.background.paper,
                                        border: '1px solid rgba(255,126,185,0.15)',
                                        boxShadow: '0 4px 24px rgba(255,126,185,0.08)',
                                        overflow: 'hidden', cursor: 'grab',
                                        opacity: draggedIndex === index ? 0.5 : 1,
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            transform: draggedIndex === index ? 'rotate(3deg)' : 'translateY(-6px)',
                                            boxShadow: '0 16px 48px rgba(255,126,185,0.2)',
                                            borderColor: 'rgba(255,126,185,0.4)'
                                        },
                                        '&:active': { cursor: 'grabbing' }
                                    }}
                                >
                                    {/* Accent bar */}
                                    <Box sx={{
                                        height: 5,
                                        background: 'linear-gradient(90deg, #ff7eb9, #AB47BC)',
                                        flexShrink: 0
                                    }} />

                                    {/* ✅ CardContent so minHeight za ednakvi kartichki */}
                                    <CardContent sx={{
                                        p: { xs: 3, md: 4 },
                                        flexGrow: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'space-between',
                                        gap: 2
                                    }}>
                                        {/* ✅ flexGrow: 1 - gorna polovina raste */}
                                        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                                            {/* Top row */}
                                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <DragIndicator sx={{ color: '#ff7eb9', fontSize: 20 }} />
                                                    <Chip label="Диета" size="small" sx={{
                                                        bgcolor: 'rgba(255,126,185,0.1)', color: '#ff7eb9',
                                                        fontWeight: 'bold', fontSize: '0.7rem',
                                                        border: '1px solid rgba(255,126,185,0.3)'
                                                    }} />
                                                </Box>
                                                <Box display="flex" gap={0.5}>
                                                    <IconButton size="small"
                                                                onClick={() => handleOpenDialog(diet)}
                                                                sx={{
                                                                    color: '#a5d8ff',
                                                                    '&:hover': { bgcolor: 'rgba(165,216,255,0.15)', transform: 'scale(1.1)' },
                                                                    transition: 'all 0.2s ease'
                                                                }}>
                                                        <Edit fontSize="small" />
                                                    </IconButton>
                                                    <IconButton size="small"
                                                                onClick={() => setDeleteConfirm({ open: true, diet })}
                                                                sx={{
                                                                    color: '#ff6b6b',
                                                                    '&:hover': { bgcolor: 'rgba(255,107,107,0.1)', transform: 'scale(1.1)' },
                                                                    transition: 'all 0.2s ease'
                                                                }}>
                                                        <Delete fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            </Box>

                                            {/* Avatar + Title */}
                                            <Box display="flex" alignItems="center" gap={2} mb={2}>
                                                <Avatar sx={{
                                                    background: 'linear-gradient(135deg, #ff7eb9, #AB47BC)',
                                                    width: 48, height: 48,
                                                    boxShadow: '0 4px 12px rgba(255,126,185,0.3)',
                                                    flexShrink: 0
                                                }}>
                                                    <Restaurant />
                                                </Avatar>
                                                <Typography variant="h5" sx={{
                                                    fontWeight: 800,
                                                    color: theme.palette.text.primary,
                                                    fontSize: { xs: '1.1rem', md: '1.3rem' },
                                                    lineHeight: 1.3
                                                }}>
                                                    {diet.name}
                                                </Typography>
                                            </Box>

                                            {/* Divider */}
                                            <Box sx={{
                                                width: 40, height: 3, borderRadius: 2,
                                                background: 'linear-gradient(90deg, #ff7eb9, #AB47BC)',
                                                mb: 2
                                            }} />

                                            {/* ✅ Description so minHeight - sekogash ista visina */}
                                            {/* ✅ Description (supports new lines + empty lines) */}
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: theme.palette.text.secondary,
                                                    lineHeight: 1.7,
                                                    whiteSpace: 'pre-wrap',   // ✅ ги прикажува \n како нов ред
                                                    wordBreak: 'break-word'   // ✅ да не крши layout со долги зборови/линкови
                                                }}
                                            >
                                                {diet.description || 'Нема опис'}
                                            </Typography>
                                        </Box>

                                        {/* ✅ Price - sekogash dolu */}
                                        <Box sx={{
                                            textAlign: 'center', py: 2,
                                            borderRadius: '12px',
                                            background: 'linear-gradient(135deg, rgba(255,126,185,0.06), rgba(171,71,188,0.06))',
                                            border: '1px solid rgba(255,126,185,0.12)',
                                            mt: 'auto' // ✅ sekogash na dno
                                        }}>
                                            <Typography variant="h4" sx={{
                                                fontWeight: 900,
                                                background: 'linear-gradient(45deg, #ff7eb9, #AB47BC)',
                                                WebkitBackgroundClip: 'text',
                                                WebkitTextFillColor: 'transparent',
                                                fontSize: { xs: '1.6rem', md: '2rem' }
                                            }}>
                                                {diet.price} ден
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                                                месечно
                                            </Typography>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Container>

            {/* ===== ADD / EDIT DIALOG ===== */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: '20px',
                            backgroundColor: theme.palette.background.paper,
                            border: '2px solid rgba(255,126,185,0.3)'
                        }
                    }}>
                <DialogTitle sx={{
                    background: 'linear-gradient(45deg, #ff7eb9, #AB47BC)',
                    color: 'white', py: 2.5
                }}>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Restaurant />
                        <Typography variant="h5" fontWeight="bold">
                            {editingDiet ? '✏️ Уреди Диета' : '🥗 Додади Нова Диета'}
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ mt: 3 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <TextField fullWidth label="Име на диета *"
                                       value={formData.name}
                                       onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                       error={!!formErrors.name} helperText={formErrors.name}
                                       sx={fieldSx} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth label="Опис *" multiline rows={4}
                                       value={formData.description}
                                       onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                       error={!!formErrors.description} helperText={formErrors.description}
                                       sx={fieldSx} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth label="Цена (денари) *" type="number"
                                       value={formData.price}
                                       onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                                       error={!!formErrors.price} helperText={formErrors.price}
                                       inputProps={{ min: 0 }} sx={fieldSx} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 3, gap: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
                    <Button onClick={handleCloseDialog} variant="outlined"
                            sx={{
                                borderRadius: '10px', px: 3,
                                borderColor: theme.palette.divider,
                                color: theme.palette.text.secondary,
                                '&:hover': { borderColor: '#ff7eb9', color: '#ff7eb9' }
                            }}>
                        Откажи
                    </Button>
                    <Button onClick={handleSaveDiet} variant="contained"
                            disabled={saving}
                            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
                            sx={{
                                background: 'linear-gradient(45deg, #ff7eb9, #AB47BC)',
                                borderRadius: '10px', px: 4, fontWeight: 'bold',
                                '&:hover': { background: 'linear-gradient(45deg, #ff4a97, #9C27B0)' }
                            }}>
                        {saving ? 'Се зачувува...' : editingDiet ? 'Ажурирај' : 'Додади'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ===== DELETE DIALOG ===== */}
            <Dialog open={deleteConfirm.open}
                    onClose={() => setDeleteConfirm({ open: false, diet: null })}
                    PaperProps={{
                        sx: {
                            borderRadius: '16px',
                            backgroundColor: theme.palette.background.paper,
                            border: '2px solid rgba(255,126,185,0.3)'
                        }
                    }}>
                <DialogTitle sx={{
                    background: 'linear-gradient(45deg, #ff7eb9, #AB47BC)',
                    color: 'white', fontWeight: 'bold', textAlign: 'center'
                }}>
                    ⚠️ Потврди бришење
                </DialogTitle>
                <DialogContent sx={{ pt: 3, textAlign: 'center' }}>
                    <Typography sx={{ color: theme.palette.text.primary, mt: 1 }}>
                        Дали сте сигурни дека сакате да ја избришете диетата
                    </Typography>
                    <Typography sx={{ color: '#ff7eb9', fontWeight: 'bold', fontSize: '1.1rem', my: 1 }}>
                        "{deleteConfirm.diet?.name}"?
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                        Оваа акција е неповратна!
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3 }}>
                    <Button onClick={() => setDeleteConfirm({ open: false, diet: null })}
                            disabled={deleteLoading}
                            sx={{
                                borderRadius: '10px', px: 3,
                                color: theme.palette.text.secondary,
                                '&:hover': { color: '#ff7eb9' }
                            }}>
                        Откажи
                    </Button>
                    <Button onClick={handleDeleteDiet} variant="contained"
                            disabled={deleteLoading}
                            startIcon={deleteLoading ? <CircularProgress size={20} color="inherit" /> : <Delete />}
                            sx={{
                                background: 'linear-gradient(45deg, #ff7eb9, #AB47BC)',
                                borderRadius: '10px', px: 3, fontWeight: 'bold',
                                '&:hover': { background: 'linear-gradient(45deg, #ff4a97, #9C27B0)' }
                            }}>
                        {deleteLoading ? 'Се брише...' : 'Избриши'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ===== SNACKBAR ===== */}
            <Snackbar open={snackbar.open} autoHideDuration={4000}
                      onClose={() => setSnackbar({ ...snackbar, open: false })}>
                <Alert severity={snackbar.severity}
                       onClose={() => setSnackbar({ ...snackbar, open: false })}
                       sx={{ borderRadius: '12px', fontWeight: 'bold' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default AdminDiets;